import {
  HttpContextToken,
  type HttpEvent,
  type HttpInterceptorFn,
  HttpResponse,
} from '@angular/common/http';
import { inject } from '@angular/core';
import type { Observable } from 'rxjs';
import { tap } from 'rxjs';
import { KundeTokenService, decodeJwtExp } from '../services/kunde-token.service';

/**
 * Set when the customer JWT was actually attached to this request.
 *
 * A 401 is only a session to renew when a token was sent; without one the
 * caller was never authenticated (an anonymous request, or a claimed task
 * opened from a fresh browser) and only the caller knows what to do about it.
 * Deciding that is application policy, so it lives app-side — this token is how
 * an interceptor wrapping this one learns the fact. `HttpRequest.clone()` shares
 * the context object by reference, so a wrapping interceptor reads the value
 * this one wrote, even though it ran first.
 */
export const KUNDE_TOKEN_SENT = new HttpContextToken(() => false);

/**
 * The customer JWT belongs on backend calls only, in both directions:
 * `MatIconRegistry` fetches SVG assets through the same `HttpClient`, and those
 * must neither carry the token outbound nor be able to replace it inbound.
 *
 * A convention, not a security boundary — every caller happens to sit under
 * `BASE_PATH` today, but this is a host-blind substring test. Testing the
 * configured API origin would need that origin injected here.
 */
function isApiRequest(url: string): boolean {
  return url.includes('/api/');
}

/**
 * Attaches the customer token to backend calls and keeps any refreshed token
 * the backend hands back.
 */
export const kundeTokenInterceptor: HttpInterceptorFn = (req, next) => {
  if (!isApiRequest(req.url)) return next(req);

  const kundeTokenService = inject(KundeTokenService);
  const token = kundeTokenService.getToken();

  if (!token) return next(req).pipe(keepRefreshedToken(kundeTokenService, false));

  req.context.set(KUNDE_TOKEN_SENT, true);

  return next(req.clone({ setHeaders: { kundeToken: token } })).pipe(
    keepRefreshedToken(kundeTokenService, true)
  );
};

/** Adopts a `kundeToken` response header as the current token. */
function keepRefreshedToken(
  kundeTokenService: KundeTokenService,
  tokenSent: boolean
): (source: Observable<HttpEvent<unknown>>) => Observable<HttpEvent<unknown>> {
  return source =>
    source.pipe(
      tap(event => {
        if (!(event instanceof HttpResponse)) return;

        const refreshed = event.headers.get('kundeToken');
        if (!refreshed) return;

        const gueltigBis = decodeJwtExp(refreshed);
        if (!gueltigBis) return;

        // A zugriffToken restore has no prior session: the backend's response
        // header is the first token, so setToken establishes it. An existing
        // session just slides its expiry forward via refreshToken.
        if (kundeTokenService.getToken()) {
          kundeTokenService.refreshToken(refreshed, gueltigBis);
        } else if (!tokenSent) {
          kundeTokenService.setToken(refreshed, gueltigBis);
        }
      })
    );
}
